import type { CSSVariablesResolver, MantineColorsTuple, MantineThemeOverride } from '@mantine/core';
import { Button, createTheme, mergeThemeOverrides, Notification, NumberInput } from '@mantine/core';

const notificationColorMap: Record<string, string> = {
  red: 'danger',
  orange: 'warning',
  green: 'success',
  blue: 'info'
};

const externoPrimary: MantineColorsTuple = [
  '#ebf0fd',
  '#d5e0fb',
  '#a8bef5',
  '#789af0',
  '#507beb',
  '#3768e8',
  '#2c5fe8',
  '#1f4fcf',
  '#1847bd',
  '#0a3bab'
];

// Neutralna skala izvučena iz Figma dizajn tokena (bg-canvas, bg-surface-2, border-soft,
// border-default, text-muted, text-secondary, text-primary/accent-secondary — indeksi 4, 6, 8
// interpolirani da popune Mantine-ovu skalu od 10 nijansi)
const externoGray: MantineColorsTuple = [
  '#f3f6f9',
  '#e5ebf1',
  '#d2dce5',
  '#b4c2cf',
  '#93a5b5',
  '#71879b',
  '#5a6f82',
  '#435669',
  '#2f405a',
  '#1b2a4a'
];

// Zlatna accent-primary iz Figma dizajna (#c49b3c na indeksu 6, Mantine-ov default primaryShade)
const externoGold: MantineColorsTuple = [
  '#f9f5ec',
  '#f0e6ce',
  '#e4d2a7',
  '#d9be80',
  '#cfad5f',
  '#c9a34c',
  '#c49b3c',
  '#a78433',
  '#896d2a',
  '#6c5521'
];

export const theme = createTheme({
  primaryColor: 'gold',
  colors: {
    externo: externoPrimary,
    gray: externoGray,
    gold: externoGold
  },
  fontFamily: 'IBM Plex Sans, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
  defaultRadius: 'sm',
  radius: {
    sm: '6px',
    lg: '12px'
  },
  components: {
    NumberInput: NumberInput.extend({
      defaultProps: {
        inputMode: 'decimal'
      }
    }),
    Button: Button.extend({
      defaultProps: {
        radius: 'sm'
      },
      styles: (_theme, props) => {
        const isDanger = props.color === 'red';
        const isFilled = !props.variant || props.variant === 'filled';
        return {
          root: {
            // Jedna visina za gumbe i polja: 36 web / 44 mobile (global.css --button-height-default),
            // Small 28.
            height: props.size === 'xs' ? 28 : 'var(--button-height-default)',
            paddingInline: props.size === 'xs' ? undefined : 20,
            fontSize: props.size === 'xs' ? undefined : 14,
            fontWeight: 600,
            ...(props.variant === 'outline' && {
              borderColor: isDanger ? 'var(--color-status-danger)' : 'var(--color-border-default)',
              color: isDanger ? 'var(--color-status-danger)' : 'var(--color-text-primary)'
            }),
            ...(props.variant === 'default' && {
              backgroundColor: 'var(--color-bg-surface)',
              borderColor: 'var(--color-border-default)',
              color: 'var(--color-text-primary)'
            }),
            ...(props.variant === 'light' && {
              backgroundColor: 'var(--color-accent-secondary-soft)',
              color: 'var(--color-text-primary)'
            }),
            ...(props.variant === 'subtle' && isDanger && { color: 'var(--color-status-danger)' }),
            ...(isFilled && {
              backgroundColor: isDanger
                ? 'var(--color-status-danger-solid)'
                : 'var(--color-accent-primary)',
              color: isDanger ? 'var(--color-text-on-secondary)' : 'var(--color-text-on-accent)'
            }),
            // Boje iznad pregaze Mantineov izgled onemogućenog gumba — bez ovoga onemogućen gumb
            // (npr. "Spremi stavke" dok stavka ima grešku) izgleda aktivno.
            ...(props.disabled && {
              backgroundColor: 'var(--color-bg-surface-2)',
              borderColor: 'var(--color-border-soft)',
              color: 'var(--color-text-muted)',
              cursor: 'not-allowed'
            })
          }
        };
      }
    }),
    Paper: {
      defaultProps: {
        shadow: 'xs'
      },
      styles: {
        root: {
          backgroundColor: 'var(--color-bg-surface)',
          borderColor: 'var(--color-border-soft)'
        }
      }
    },
    Notification: Notification.extend({
      defaultProps: {
        radius: 8,
        withBorder: true
      },
      styles: (_theme, props) => {
        const status = notificationColorMap[props.color ?? ''] ?? 'info';
        return {
          root: {
            backgroundColor: `var(--color-status-${status}-soft)`,
            borderColor: `var(--color-status-${status})`,
            boxShadow: 'none'
          },
          icon: {
            backgroundColor: 'transparent',
            color: `var(--color-status-${status})`
          },
          title: {
            color: `var(--color-status-${status})`,
            fontWeight: 600,
            fontSize: 13
          },
          description: {
            color: `var(--color-status-${status})`,
            fontSize: 13
          },
          closeButton: {
            color: `var(--color-status-${status})`
          }
        };
      }
    })
  }
});

// Decimalni separator u poljima za brojeve prati jezik (en-GB '.', hr-HR/de-DE ','). U hr/de se pri
// upisu prihvaća i '.' (pretvara se u ','), a u en ',' ostaje separator tisućica i ne pretvara se.
export const localizedTheme = (locale: string): MantineThemeOverride => {
  const decimalSeparator =
    new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal')
      ?.value ?? '.';
  return mergeThemeOverrides(
    theme,
    createTheme({
      components: {
        NumberInput: NumberInput.extend({
          defaultProps: {
            decimalSeparator,
            allowedDecimalSeparators: decimalSeparator === ',' ? [',', '.'] : ['.']
          }
        })
      }
    })
  );
};

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: { '--mantine-color-placeholder': 'var(--color-text-muted)' },
  dark: { '--mantine-color-placeholder': 'var(--color-text-muted)' }
});
